import './ActionReasonModal.scss';
import { type ReactNode, useId } from 'react';
import { useForm } from 'react-hook-form';
import DOMPurify from 'dompurify';
import ModalContainer, { IModalContainerProps } from '@molecules/ModalContainer/ModalContainer';
import TextareaControl from '@srm/ui/controls/TextareaControl';
import InputControl from '@srm/ui/controls/InputControl';
import { requiredTextRules } from '@srm/ui/lib/requiredTextRules';
import { stringifyError } from '@srm/core/errors';
import { useUnsavedFormInput } from '@srm/ui/hooks/useUnsavedFormInput';
import { ModalSize } from '@srm/ui/types/ModalSize';
import { useActionSubmit } from '@organisms/actions/useActionSubmit';
import {
    ACTION_REASON_MAX_LENGTH,
    ACTION_TICKET_MAX_LENGTH,
    actionReasonBody,
    IActionReason,
} from './actionReason';

export interface IActionReasonModalProps<T = unknown> {
    header: string;
    /** Вопрос или пояснение кнопки над полем причины; HTML, как в `MessageDialog`. */
    message?: string;
    /** Подпись поля причины; по умолчанию «Причина». */
    reasonLabel?: string;
    okButtonText: string;
    cancelButtonText: string;
    modalSize: ModalSize;
    /** Запрос действия с причиной. */
    submit: (reason: IActionReason) => Promise<T>;
    completeHandler?: (isSucceed: boolean) => void;
    /** Результат удачного действия; задан — окно не вызывает `completeHandler(true)` само. */
    onSuccess?: (result: T) => void;
    /** Поля и пояснения действия между вопросом и причиной: параметры, что будет сделано, предупреждения. */
    children?: ReactNode;
    /** Действие ещё нельзя выполнить: параметры не заполнены или не отмечено обязательное подтверждение. */
    okButtonDisabled?: boolean;
}

/**
 * Окно действия с обязательной причиной (`requireReason` у кнопки): причина и номер обращения уходят в аудит,
 * журнал событий и пометки — «Ход маршрута» и история карточки покажут, кто и почему это сделал.
 */
export const ActionReasonModal = <T,>(props: IActionReasonModalProps<T>) => {
    const formMethods = useForm<IActionReason>({ mode: 'onBlur', defaultValues: { reason: '', ticketRef: '' } });
    const hasUnsavedInput = useUnsavedFormInput(formMethods, { textFields: ['reason', 'ticketRef'] });

    const { errorText, isBusy, isSubmitting, okClick, cancelClick } = useActionSubmit({
        completeHandler: props.completeHandler,
        onSuccess: props.onSuccess,
        initialBusy: false,
        resetErrorOnSubmit: true,
        mapError: stringifyError,
        submit: (form: IActionReason) => props.submit(actionReasonBody(form)),
    });

    const messageId = useId();
    const modalContainerProps: IModalContainerProps = {
        header: props.header,
        'aria-describedby': props.message ? messageId : undefined,
        size: props.modalSize,
        showOkButton: true,
        showCancelButton: true,
        okButtonText: props.okButtonText,
        okButtonDisabled: props.okButtonDisabled,
        cancelButtonText: props.cancelButtonText,
        loading: isBusy,
        closeDisabled: isSubmitting,
        hasUnsavedInput,
        errorText,
        formMethods,
        onSubmit: okClick,
        onCancel: cancelClick,
    };

    return (
        <ModalContainer {...modalContainerProps}>
            <div className="action-reason">
                {props.message ? (
                    <div
                        id={messageId}
                        className="action-reason__message"
                        dangerouslySetInnerHTML={{
                            __html: DOMPurify.sanitize(props.message, { ADD_ATTR: ['target'] }),
                        }}
                    />
                ) : null}
                {props.children}
                <TextareaControl
                    name="reason"
                    label={props.reasonLabel || 'Причина'}
                    required
                    rows={4}
                    maxLength={ACTION_REASON_MAX_LENGTH}
                    rules={requiredTextRules('Укажите причину')}
                    control={formMethods.control}
                    data-testid="action-reason-input"
                />
                <InputControl
                    name="ticketRef"
                    label="Номер обращения"
                    maxLength={ACTION_TICKET_MAX_LENGTH}
                    control={formMethods.control}
                />
            </div>
        </ModalContainer>
    );
};
